import React, { memo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { text, borderRadius, dataVizLayout } from "@atlas-int/tokens";
import { TooltipPosition, TooltipTheme } from "./tooltip/SharedTooltipComponents";
import TooltipPortal from "./TooltipPortal";

// ── Styled wrappers (hoisted, stable identity) ──────────────────────

const GenericSpan = styled.span`
  cursor: help;
  display: contents;
`;

const InlineSpan = styled(GenericSpan)`
  border-bottom: 1px dotted ${text.primary};
  display: inline-block;
`;

const TriggerDot = styled.span`
  cursor: help;
  width: ${dataVizLayout.tooltipTriggerSize};
  height: ${dataVizLayout.tooltipTriggerSize};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.pill};
  margin: ${dataVizLayout.tooltipTriggerMargin};
`;

// ── Types ────────────────────────────────────────────────────────────

export interface TooltipTriggerProps {
  /** Content to display in the tooltip. */
  explanation: React.ReactNode | null;
  /** Element(s) that trigger the tooltip on hover. Omit for a standalone dot. */
  children?: React.ReactNode;
  /** Render as inline text with a dotted underline. */
  inline?: boolean;
  /** Render trigger text in italics. */
  italic?: boolean;
  cursor?: string;
  /** Delay in ms before showing the tooltip. */
  delay?: number;
  theme?: TooltipTheme;
  tooltipPosition?: TooltipPosition;
}

// ── Component ────────────────────────────────────────────────────────

/**
 * Wraps children (or renders a trigger dot) and shows a positioned
 * tooltip on hover.  Intended for inline label tooltips in settings
 * panels, metric explanations inside tooltip content, etc.
 *
 * For chart-level tooltips that need isolation from SVG rerenders, use
 * {@link TooltipStateBridge} + {@link TooltipStateIsolationProvider}.
 */
const TooltipTrigger = memo(function TooltipTrigger({
  explanation,
  children,
  inline,
  italic,
  cursor,
  delay,
  theme,
  tooltipPosition,
}: TooltipTriggerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<number | undefined>(undefined);

  const onMouseEnter = (e: React.MouseEvent) => {
    setCoords({ top: e.clientY, left: e.clientX });
    if (delay) {
      timeoutRef.current = setTimeout(
        () => setIsVisible(true),
        delay,
      ) as unknown as number;
    } else {
      setIsVisible(true);
    }
  };

  const onMouseMoveCapture = (e: React.MouseEvent) => {
    if (!isVisible && explanation) setIsVisible(true);
    setCoords({ top: e.clientY, left: e.clientX });
  };

  const onMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  const portal =
    isVisible && explanation ? (
      <TooltipPortal
        explanation={explanation}
        coords={coords}
        theme={theme}
        tooltipPosition={tooltipPosition}
      />
    ) : null;

  if (children !== undefined) {
    const Wrapper = inline ? InlineSpan : GenericSpan;
    return (
      <Wrapper
        onMouseEnter={onMouseEnter}
        onMouseMoveCapture={onMouseMoveCapture}
        onMouseLeave={onMouseLeave}
        style={{ cursor, fontStyle: italic ? "italic" : "inherit" }}
      >
        {children}
        {portal}
      </Wrapper>
    );
  }

  return (
    <TriggerDot
      onMouseEnter={onMouseEnter}
      onMouseMoveCapture={onMouseMoveCapture}
      onMouseLeave={onMouseLeave}
      style={{ cursor }}
    >
      {portal}
    </TriggerDot>
  );
});

export default TooltipTrigger;
